import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import Loader from 'Components/Loader'
import { DialogModal } from 'Components/UIComponents'
import LayoutContainer from 'Components/LayoutComponent'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { TABLE_CONST } from 'Utils/Constants'
import EditCountryWiseProductionReport from './EditCountryWiseProductionReport'
import AddCountryWiseProductionReport from './AddCountryWiseProductionReport'

const productionColumns = [
  { field: 'year', header: 'Year' },
  { field: 'totalProduction', header: 'Total Production' },
  { field: 'arabica', header: 'Arabica' },
  { field: 'robusta', header: 'Robusta' },
  { field: 'exports', header: 'Exports' },
  { field: 'consumption', header: 'Consumption' }
]

const CountryWiseProductionReport = ({ setLoading, mockCountryWiseExportReport, mockColumns, mockExportReportData }) => {
  const { t } = useTranslation()
  let rowCount = 8
  let paginationButtons = TABLE_CONST.paginationButtonCount
  const getColumnsWithActions = () => [
    ...productionColumns,
    {
      field: 'actions',
      header: t('ACTIONS')
    }
  ]

  const [columns, setColumns] = useState([])
  const [countryProductionRawData, setCountryProductionRawData] = useState([])
  const [productionReportData, setProductionReportData] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [selectedCountryId, setSelectedCountryId] = useState(0)
  const [yearOptions, setYearOptions] = useState([])
  const [selectedYear, setSelectedYear] = useState(null)
  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedRowData, setSelectedRowData] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [errorModal, setErrorModal] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {} })

  useEffect(() => {
    fetchProductionReportData()

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  useEffect(() => {
    handleReportData()
  }, [selectedYear])

  const parseProductionApiResponse = (response) => {
    const returnList = response?.data?.returnLst || {}
    const dataList = returnList?.data || []
    const countryList = returnList?.countryList || []

    const flattenedData = dataList.flatMap((countryItem) => {
      const reportRows = countryItem?.reportData || []

      return reportRows.map((report) => ({
        ...report,
        countryId: countryItem?.idCountry,
        countryName: countryItem?.countryName,
        consumption: report?.consumption ?? report?.consumptions
      }))
    })

    const dropdownOptions = [{ id: 0, nicename: 'All Countries', iso3: '' }, ...countryList]

    return {
      flattenedData,
      dropdownOptions
    }
  }

  const fetchProductionReportData = async () => {
    handleDismiss()
    setLoading(true)
    const retryFunction = () => fetchProductionReportData()

    try {
      const productionResponse = await apiAdapterCoffeeWeb.getCountryWiseProuctionReportsByCountryId({ countryId: 0 })
      const { flattenedData, dropdownOptions } = parseProductionApiResponse(productionResponse)
      const distinctYears = [...new Set(flattenedData.map((item) => item?.year).filter(Boolean))]
      const yearDropdownOptions = distinctYears.map((year) => ({ year: `${year}` }))

      setColumns(getColumnsWithActions())
      setCountryProductionRawData(flattenedData)
      setProductionReportData(flattenedData)
      setCountryOptions(dropdownOptions)
      setSelectedCountryId(0)
      setYearOptions(yearDropdownOptions)
      setSelectedYear(null)
    } catch {
      handleApiError('NO_DATA_FOUND', retryFunction)
    } finally {
      setLoading(false)
    }
  }

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  const handleWindowResize = () => {
    setWindowSize(window.innerWidth)
  }

  const handleApiError = (errorMessage, onRetry) => {
    setErrorModal({
      ...errorModal,
      showModal: true,
      modalType: 'error',
      message: t(errorMessage),
      onRetry: onRetry,
      onHide: handleDismiss
    })
  }

  const handleReportData = (data = countryProductionRawData) => {
    const filteredData = data.filter((item) => {
      const yearMatch = selectedYear?.year ? `${item?.year}` === `${selectedYear?.year}` : true

      return yearMatch
    })

    setColumns(getColumnsWithActions())
    setProductionReportData(filteredData)
  }

  const handleCountryChange = async (event) => {
    const countryId = event.target.value

    setSelectedCountryId(countryId)
    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getCountryWiseProuctionReportsByCountryId({ countryId: countryId ?? 0 })
      const { flattenedData, dropdownOptions } = parseProductionApiResponse(response)
      const distinctYears = [...new Set(flattenedData.map((item) => item?.year).filter(Boolean))]

      setYearOptions(distinctYears.map((year) => ({ year: `${year}` })))
      setSelectedYear(null)
      setColumns(getColumnsWithActions())
      setCountryProductionRawData(flattenedData)
      setProductionReportData(flattenedData)
      setCountryOptions(dropdownOptions)
    } catch {
      handleApiError('NO_DATA_FOUND', () => handleCountryChange(event))
    } finally {
      setLoading(false)
    }
  }

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }

  const handleDismiss = () => {
    setErrorModal({ ...errorModal, showModal: false })
  }

  const handleAddButtonClick = () => {
    setShowForm(true)
  }

  const refreshAllCountriesData = async () => {
    setSelectedCountryId(0)
    setSelectedYear(null)
    await fetchProductionReportData()
  }

  const handleEdit = (rowData) => {
    setShowEditModal(true)
    setSelectedRowData(rowData)
  }

  const deleteReportData = async (rowData) => {
    setLoading(true)
    setErrorModal({ ...errorModal, showModal: false })
    const retryFunction = () => deleteReportData(rowData)

    try {
      const response = await apiAdapterCoffeeWeb.deleteCountryWiseProductionRepor({ id: rowData?.id })

      if (response?.status === 200 || response?.data?.statusCode === 200) {
        refreshAllCountriesData()
        setErrorModal({
          ...errorModal,
          showModal: true,
          modalType: 'success',
          message: t('REPORT_DATA_DELETED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleDismiss
        })
      } else {
        handleApiError('NO_DATA_FOUND', retryFunction)
      }
    } catch {
      handleApiError('NO_DATA_FOUND', retryFunction)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (rowData) => {
    setErrorModal({
      ...errorModal,
      showModal: true,
      modalType: 'info',
      message: t('DELETE_USDA_COFFEE_REPORT'),
      onConfirmation: () => deleteReportData(rowData),
      onHide: handleDismiss
    })
  }

  return (
    <>
      <div className="production-report-container">
        <LayoutContainer title={t('COUNTRY_WISE_PRODUCTION_REPORT')} breadCrumbsTitle={'countryProductionReport'} showAddButton={true} handleAddButtonClick={handleAddButtonClick}>
          <div className="dropdown-wrapper">
            <Dropdown value={selectedCountryId} options={countryOptions} optionLabel="nicename" optionValue="id" onChange={handleCountryChange} placeholder={t('SELECT_COUNTRY')} className="country-dropdown" data-testid="country-dropdown" />
            <Dropdown value={selectedYear} options={yearOptions} optionLabel="year" onChange={handleYearChange} placeholder={t('SELECT_YEAR')} className="year-dropdown" data-testid="year-dropdown" />
          </div>
          <div className="data-table">
            <DataTable value={productionReportData} paginator={productionReportData?.length} rows={rowCount} pageLinkSize={paginationButtons} columns={columns} emptyMessage={t('NO_DATA_FOUND_MESSAGE')}>
              {columns.map((col) => {
                if (col.field === 'actions') {
                  return (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      className="table-action-column"
                      body={(rowData) => (
                        <div className="actions-column">
                          <Button type="button" icon="pi pi-pencil" className="edit-button" tooltip={t('EDIT')} tooltipOptions={{ position: 'top' }} onClick={() => handleEdit(rowData)} data-testid="edit-button" />
                          <Button type="button" icon="pi pi-trash" className="deleted-button" tooltip={t('DELETE')} tooltipOptions={{ position: 'top' }} onClick={() => handleDelete(rowData)} data-testid="delete-button" />
                        </div>
                      )}
                    />
                  )
                }

                return <Column key={col.field} field={col.field} header={col.header} />
              })}
            </DataTable>
          </div>
          <DialogModal {...errorModal} onDismiss={handleDismiss} />
          <AddCountryWiseProductionReport showForm={showForm} setShowForm={setShowForm} onSuccess={refreshAllCountriesData} countryOptions={countryOptions} />
          <EditCountryWiseProductionReport showEditModal={showEditModal} setShowEditModal={setShowEditModal} rowData={selectedRowData} onSuccess={refreshAllCountriesData} countryOptions={countryOptions} />
        </LayoutContainer>
      </div>
    </>
  )
}

export default Loader(CountryWiseProductionReport)
